import type { HLocation } from '@reach/router';
import qs from 'query-string';

import { type Language, languageToFBLang } from './Language';
import { ConsentGroup } from './OneTrust';
import { ScriptLoader } from './Script';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace FacebookSDK {
    interface InitOptions {
      appId: string;
      version: string;
      xfbml?: boolean;
      autoLogAppEvents?: boolean;
    }
    interface LoginOptions {
      appId: string;
      version?: string;
      scope?: string;
    }
    interface AuthResponse {
      accessToken?: string;
      error?: Error;
    }
    interface LoginStatus {
      status: 'connected' | string;
      authResponse: AuthResponse;
    }

    export interface FB {
      init(opts: InitOptions): void;
      Event: {
        subscribe(name: string, callback: () => void): void;
      };
      getLoginStatus(callback: (status: LoginStatus) => void): void;
      login(callback: (response: AuthResponse) => void, options: LoginOptions): void;
    }
  }

  interface Window {
    FB?: FacebookSDK.FB;
    fbAsyncInit?: () => void;
  }
}

const callbacks = new Set<() => void>();

let facebookSDKLoader: ScriptLoader;

export const isFacebookSDKLoaded = () => facebookSDKLoader?.isLoaded ?? false;
export const isFacebookSDKBlocked = () => facebookSDKLoader?.isBlocked ?? true;

export function addFacebookSDKCallback(callback: () => void) {
  callbacks.add(callback);
  return () => {
    callbacks.delete(callback);
  };
}

export function loadFacebookSDK(language: Language, callback?: () => void): void | (() => void) {
  if (!facebookSDKLoader) {
    facebookSDKLoader = new ScriptLoader(
      `https://connect.facebook.net/${languageToFBLang(language)}/sdk.js`,
      ConsentGroup.ADVANCED_ANALYTICS,
      'facebook-jssdk',
    );

    window.fbAsyncInit = () => {
      window.FB.init({
        appId: process.env.GATSBY_FACEBOOK_APP_ID,
        xfbml: true,
        autoLogAppEvents: true,
        version: process.env.GATSBY_FACEBOOK_SDK_VERSION,
      });

      for (const callback of callbacks) {
        callback();
      }
    };
  }

  let result = undefined;
  if (callback) {
    result = facebookSDKLoader.then(callback);
  }
  facebookSDKLoader.load();
  return result;
}

export function createFacebookAuthURL(location: HLocation, scope: string | string[]): string {
  const props = qs.stringify({
    client_id: process.env.GATSBY_FACEBOOK_APP_ID,
    response_type: 'token',
    scope: Array.isArray(scope) ? scope.join(' ') : scope,
    redirect_uri: `${window.location.origin}/sign/facebook`,
    state: qs.stringify({
      original_uri: `${location.pathname}${location.search}${location.hash}`,
    }),
  });
  return `https://www.facebook.com/${process.env.GATSBY_FACEBOOK_SDK_VERSION}/dialog/oauth?${props}`;
}
